import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { MessageSquare, Send, Bug, HelpCircle, Lightbulb } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KATEGORIEN = [
  { value: "verbesserung", label: "Verbesserung", icon: Lightbulb, color: "text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800" },
  { value: "bug", label: "Fehler melden", icon: Bug, color: "text-red-500 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800" },
  { value: "frage", label: "Frage", icon: HelpCircle, color: "text-blue-500 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800" },
];

const FeedbackDialog = ({ open, onOpenChange }) => {
  const [betreff, setBetreff] = useState("");
  const [nachricht, setNachricht] = useState("");
  const [kategorie, setKategorie] = useState("verbesserung");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!nachricht.trim()) {
      toast.error("Bitte eine Nachricht eingeben");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/nachrichten`, { betreff, nachricht, kategorie }, { withCredentials: true });
      toast.success("Feedback gesendet! Danke für Ihren Vorschlag.");
      setBetreff("");
      setNachricht("");
      setKategorie("verbesserung");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare size={20} className="text-blue-500" /> Feedback senden
          </DialogTitle>
          <DialogDescription>Teilen Sie uns Ihre Ideen oder Probleme mit</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Kategorie */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Kategorie</Label>
            <div className="flex gap-2">
              {KATEGORIEN.map(k => {
                const Icon = k.icon;
                return (
                  <button
                    key={k.value}
                    onClick={() => setKategorie(k.value)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                      kategorie === k.value ? k.color + ' ring-1 ring-current' : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid={`feedback-cat-${k.value}`}
                  >
                    <Icon size={14} />
                    {k.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Betreff */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Betreff</Label>
            <Input
              value={betreff}
              onChange={(e) => setBetreff(e.target.value)}
              placeholder="Kurze Zusammenfassung"
              data-testid="feedback-betreff"
            />
          </div>

          {/* Nachricht */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Nachricht *</Label>
            <textarea
              value={nachricht}
              onChange={(e) => setNachricht(e.target.value)}
              placeholder="Beschreiben Sie Ihren Vorschlag oder das Problem..."
              rows={4}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
              data-testid="feedback-nachricht"
            />
          </div>

          <div className="flex gap-3 pt-1">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white gap-2"
              onClick={handleSend}
              disabled={sending}
              data-testid="feedback-send-btn"
            >
              <Send size={16} />
              {sending ? "Senden..." : "Absenden"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
